// nexilinkAgentTransport — Nexilink's implementation of jasit-agent-kit's
// `ChatTransport`. This is the ONLY place Nexilink's V2 chat wire format
// (its own /api/link/agent/* endpoints + snake_case SSE) meets the
// generic kit. The kit core never knows about any of this — a Wails/desktop
// build would supply a totally different (in-process) ChatTransport and the
// rest of the kit wouldn't change.

import type {
    ChatApprovalDecision,
    ChatAttachment,
    ChatEvent,
    ChatAskUserFormAnswer,
    ChatTransport,
} from 'jasit-agent-kit';
import {
    linkAgentApi,
    type LinkAgentAttachment,
    type LinkAgentReference,
    type LinkAgentStreamEvent,
} from './linkAgentApi';

/** Decode a base64 artifact body to UTF-8 text (markdown/JSON). */
function decodeBase64Utf8(b64: string): string | undefined {
    try {
        const binary = atob(b64);
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    } catch {
        return undefined;
    }
}

/**
 * Map one Nexilink SSE event to the kit's ChatEvent. Pure + exported so it can
 * be unit-tested in isolation. Returns null for events with no kit equivalent.
 */
export function mapNexilinkEvent(ev: LinkAgentStreamEvent): ChatEvent | null {
    switch (ev.event) {
        case 'started':
            return { type: 'started', sessionId: ev.data.session_id };
        case 'text':
            return { type: 'text', text: ev.data.text };
        case 'tool_start':
            return {
                type: 'tool_start',
                tool: { name: ev.data.name, args: ev.data.args, description: ev.data.description },
            };
        case 'tool_end':
            return {
                type: 'tool_end',
                tool: { name: ev.data.name },
                resultLabel: ev.data.result_label,
                // #242 — forward the raw tool result so read-tool results can be
                // rendered as rich entity cards (all Nexilink read tools return a
                // JSON/markdown string). Previously dropped.
                result: ev.data.result != null ? String(ev.data.result) : undefined,
            };
        case 'artifact':
            return {
                type: 'artifact',
                artifact: {
                    id: ev.data.id || ev.data.name, // single-artifact turns may omit id
                    name: ev.data.name,
                    mime: ev.data.mime,
                    content: decodeBase64Utf8(ev.data.content_b64),
                },
            };
        case 'artifact_deleted':
            return { type: 'artifact_deleted', id: ev.data.id };
        case 'approval_needed':
            return {
                type: 'approval_needed',
                request: {
                    approvals: ev.data.approvals.map((a) => ({
                        call_id: a.call_id,
                        tool_name: a.tool_name,
                        args: a.args,
                    })),
                },
            };
        case 'ask_user':
            return {
                type: 'ask_user',
                question: {
                    prompt: ev.data.question.prompt,
                    options: ev.data.question.options,
                    multi: ev.data.question.multi,
                },
            };
        case 'compacted':
            return { type: 'compacted', summary: ev.data.summary };
        case 'suggest_next':
            return { type: 'suggest_next', text: ev.data.text };
        case 'done':
            return {
                type: 'done',
                usage: ev.data.usage
                    ? {
                          promptTokens: ev.data.usage.prompt_tokens,
                          completionTokens: ev.data.usage.completion_tokens,
                          totalTokens: ev.data.usage.total_tokens,
                      }
                    : undefined,
            };
        case 'error':
            return { type: 'error', error: ev.data.error };
        default:
            return null;
    }
}

function toWireAttachment(a: ChatAttachment): LinkAgentAttachment {
    return { name: a.name, mime: a.mime, content_b64: a.contentBase64 };
}

/**
 * ChatTransport bound to one onboarding chat session. Session lifecycle
 * (start / resume) stays outside the transport — construct this once the
 * session id is known.
 */
export class NexilinkAgentTransport implements ChatTransport {
    private abort: AbortController | null = null;
    // #240 Wave 5 — the kit's ChatTransport.send(text, attachments) has no
    // references param, so the caller (useLinkAgent.send) stashes the current
    // turn's structured @-mention references here right before agent.send();
    // send() reads and clears them. Sends are sequential in this UI, so there's
    // no cross-turn race. Kept app-side (no kit change) per the layer decision.
    private pendingReferences: LinkAgentReference[] | null = null;

    // #288 Layer 2 — the meta frame of the most recent reattach() stream. Captured
    // out-of-band (the kit reducer never sees it) so the caller can read whether a
    // live turn was found (`running`), where the replay ended (`nextOffset`, for a
    // future re-reattach cursor), and whether older events were evicted (`gap`,
    // meaning the partial turn's very start is missing — history hydration covers
    // everything before it). Null until the first reattach().
    lastReattach: { running: boolean; nextOffset?: number; gap?: boolean } | null = null;

    constructor(private readonly sessionId: string) {}

    /** Attach structured references to the very next send() (one-shot). */
    setPendingReferences(refs: LinkAgentReference[] | undefined): void {
        this.pendingReferences = refs && refs.length > 0 ? refs : null;
    }

    private async *map(
        gen: AsyncGenerator<LinkAgentStreamEvent>,
    ): AsyncIterable<ChatEvent> {
        for await (const raw of gen) {
            const mapped = mapNexilinkEvent(raw);
            if (mapped) yield mapped;
        }
    }

    send(text: string, attachments?: ChatAttachment[]): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        const atts = attachments?.length ? attachments.map(toWireAttachment) : undefined;
        const refs = this.pendingReferences ?? undefined;
        this.pendingReferences = null; // one-shot: consume for this turn only
        return this.map(linkAgentApi.sendMessage(this.sessionId, text, atts, this.abort.signal, refs));
    }

    respond(decisions: ChatApprovalDecision[]): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        return this.map(
            linkAgentApi.respond(
                this.sessionId,
                {
                    decisions: decisions.map((d) => ({
                        call_id: d.call_id,
                        approve: d.approve,
                        deny_reason: d.deny_reason,
                        tool_name: d.tool_name,
                    })),
                },
                this.abort.signal,
            ),
        );
    }

    answer(answers: string[]): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        return this.map(linkAgentApi.answer(this.sessionId, { answers }, this.abort.signal));
    }

    // #111 B1 — resolve the pause with a prose reply INSTEAD of an answer. The
    // kit feature-detects on this method's presence (`canAnswerWithText`), so
    // defining it here is what turns the composer's reply-instead-of-answer
    // affordance on.
    answerWithText(text: string): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        return this.map(linkAgentApi.answerText(this.sessionId, { text }, this.abort.signal));
    }

    // #111 C — resolve a multi-question form. Positional: entry i answers
    // question i. Gated in the UI by the kit's `canAnswerForm`, which is true
    // precisely because this method exists.
    answerForm(answers: ChatAskUserFormAnswer[]): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        return this.map(linkAgentApi.answerForm(this.sessionId, { answers }, this.abort.signal));
    }

    // #300 — optional ChatTransport.compact(): user-triggered non-destructive
    // compaction. Streams the framework's `compacted` event, which the kit
    // reducer turns into the same summary divider as auto-compaction.
    compact(): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        return this.map(linkAgentApi.compact(this.sessionId, this.abort.signal));
    }

    // #288 Layer 2 — optional ChatTransport.reattach(): reattach to a RUNNING
    // backgrounded turn (fresh reload mid-stream) and replay the missed events then
    // continue live. The leading `reattach` meta frame is captured on lastReattach
    // and NOT forwarded; the replayed + live events map exactly like a normal turn,
    // so the kit rebuilds the in-flight reply. Caller (useLinkAgent) only invokes this
    // when a status poll says the turn is running (paused turns use the JSON
    // /reattach overlay instead).
    reattach(fromOffset = 0): AsyncIterable<ChatEvent> {
        this.abort = new AbortController();
        this.lastReattach = null;
        return this.mapReattach(
            linkAgentApi.reattachStream(this.sessionId, fromOffset, this.abort.signal),
        );
    }

    private async *mapReattach(
        gen: AsyncGenerator<LinkAgentStreamEvent>,
    ): AsyncIterable<ChatEvent> {
        for await (const raw of gen) {
            if (raw.event === 'reattach') {
                this.lastReattach = {
                    running: raw.data.running,
                    nextOffset: raw.data.next_offset,
                    gap: raw.data.gap,
                };
                continue; // transport-internal cursor meta — never hits the reducer
            }
            const mapped = mapNexilinkEvent(raw);
            if (mapped) yield mapped;
        }
    }

    async cancel(): Promise<void> {
        this.abort?.abort();
        this.abort = null;
        await linkAgentApi.cancel(this.sessionId);
    }
}
